import { WorkflowValidationIssueDto } from "../../types/workflowDefinitionDto";
import {
  isFieldPersonSource,
  isValidWorkflowBodyTemplate,
  WORKFLOW_END_ROUTE,
  WORKFLOW_NUMBER_ONLY_OPERATORS,
  WorkflowCanvas,
  WorkflowFormField,
  workflowMethodSendsBody,
  workflowNumberVariableTest,
} from "./workflowBuilderData";

/**
 * The Builder's Validate checklist (Epic 80429 → "Validation & messages"). The client rules run
 * first, instantly, over the canvases on screen; only a clean client pass goes on to the server's
 * POST /definition/validate (Epic 80372), which re-checks the saved draft and adds the structural
 * checks (start / reachability / dangling transitions) the client can't see. A server issue that
 * matches a client rule marks that row; any other becomes a row of its own.
 */

export type WorkflowValidationRuleId =
  | "canvas-name"
  | "opens-with-form"
  | "ends-with-finalize"
  | "form-fields"
  | "approval-approver"
  | "approval-routes"
  | "conditional-routed"
  | "routes-valid"
  | "number-comparisons"
  | "pause-unit"
  | "task-tasks"
  | "webservice-url"
  | "webservice-body"
  | "file-types";

export interface WorkflowValidationRow {
  id: string;
  label: string;
  passed: boolean;
  /** The exact failure message(s); empty when the row passed. */
  messages: string[];
}

export const WORKFLOW_VALIDATION_MESSAGES = {
  ALL_PASSED: "All checks passed",
  NEEDS_ATTENTION: "Some checks need attention",
  PUBLISH_BLOCKED: "Fix validation issues before publishing",
  VALIDATE_FIRST: "Validate the workflow first",
} as const;

const BLOCKED_FILE_TYPES = ["exe", "bat", "ps1"];

const fileTypeMessage = (label: string) =>
  `File field '${label}' allows a blocked type (exe/bat/ps1 are never allowed).`;

interface WorkflowValidationRule {
  id: WorkflowValidationRuleId;
  label: string;
  /** Server issue codes that this row stands for. */
  serverCodes: string[];
  /**
   * A per-canvas rule: true when this canvas breaks it. The checklist row aggregates it (one
   * message however many canvases fail) and the inline markers show it on each failing canvas —
   * one predicate, so the two can never disagree.
   */
  failsOn?: (canvas: WorkflowCanvas, canvases: WorkflowCanvas[], index: number) => boolean;
  message?: string;
  /** A workflow-level rule, with no single canvas to point at: messages for the checklist only. */
  check?: (canvases: WorkflowCanvas[]) => string[];
}

/** Routed: to a canvas that still exists, or deliberately to "End workflow". */
const routesTo = (canvases: WorkflowCanvas[], routeId: string) =>
  routeId === WORKFLOW_END_ROUTE || (Boolean(routeId) && canvases.some((canvas) => canvas.id === routeId));

/** Every route a canvas has chosen (unset ones excluded): gates, paths and the pause target. */
const chosenRoutes = (canvas: WorkflowCanvas): string[] => {
  const routes =
    canvas.type === "approval"
      ? [canvas.approval.acceptRouteId, canvas.approval.rejectRouteId, canvas.approval.cancelRouteId]
      : canvas.type === "conditional"
        ? canvas.paths.map((path) => path.routeId)
        : canvas.type === "pause"
          ? [canvas.pause.routeId]
          : [];
  return routes.filter((routeId) => routeId && routeId !== WORKFLOW_END_ROUTE);
};

const once = (failed: boolean, message: string) => (failed ? [message] : []);

const allowsBlockedFileType = (field: WorkflowFormField) =>
  field.dataType === "file" &&
  field.settings.accept
    .split(",")
    .map((entry) => entry.trim().replace(/^\./, "").toLowerCase())
    .some((entry) => BLOCKED_FILE_TYPES.includes(entry));

const blockedFileFields = (canvas: WorkflowCanvas) =>
  canvas.type === "form" ? canvas.fields.filter(allowsBlockedFileType) : [];

const WORKFLOW_VALIDATION_RULES: WorkflowValidationRule[] = [
  {
    id: "canvas-name",
    label: "Every canvas has a name",
    serverCodes: ["CANVAS_NO_NAME"],
    failsOn: (canvas) => !canvas.name.trim(),
    message: "Every canvas needs a name.",
  },
  {
    id: "opens-with-form",
    label: "Opens with a Form canvas",
    serverCodes: [],
    // Pointed at the first canvas, the one standing where the Form should be.
    failsOn: (canvas, _canvases, index) => index === 0 && canvas.type !== "form",
    message: "A workflow must open with a Form canvas.",
  },
  {
    id: "ends-with-finalize",
    label: "Ends with a Finalize canvas",
    serverCodes: [],
    // Nothing but a Cancel may follow Finalize: a Cancel is terminal and reached only by a route
    // (the PIF's "PIF not approved"), while anything else there would never run.
    check: (canvases) => {
      const finalizeIndex = canvases.findIndex((canvas) => canvas.type === "finalize");
      return once(
        finalizeIndex < 0 ||
          canvases.slice(finalizeIndex + 1).some((canvas) => canvas.type !== "cancel" && canvas.type !== "finalize"),
        "Every workflow must end with a Finalize canvas.",
      );
    },
  },
  {
    id: "form-fields",
    label: "Every Form canvas has ≥1 field",
    serverCodes: ["FORM_NO_FIELDS"],
    failsOn: (canvas) => canvas.type === "form" && canvas.fields.length === 0,
    message: "Every Form canvas needs at least one field.",
  },
  {
    id: "approval-approver",
    label: "Every Approval has an approver",
    serverCodes: ["APPROVAL_NO_APPROVER"],
    // The creator sources resolve by themselves; named users and field sources need their
    // reference — the users picked, or the field the person is read from.
    failsOn: (canvas) =>
      canvas.type === "approval" &&
      (canvas.approval.source === "specific-user" || isFieldPersonSource(canvas.approval.source)) &&
      !canvas.approval.approvers.trim(),
    message: "Every Approval needs an approver source.",
  },
  {
    id: "approval-routes",
    label: "Every Approval routes Accept & Reject",
    serverCodes: ["APPROVAL_NOT_ROUTED"],
    failsOn: (canvas, canvases) =>
      canvas.type === "approval" &&
      (!routesTo(canvases, canvas.approval.acceptRouteId) ||
        !routesTo(canvases, canvas.approval.rejectRouteId)),
    message: "Every Approval must route Accept and Reject.",
  },
  {
    id: "conditional-routed",
    label: "Every Conditional path is routed",
    serverCodes: ["CONDITIONAL_NOT_ROUTED"],
    failsOn: (canvas, canvases) =>
      canvas.type === "conditional" && canvas.paths.some((path) => !routesTo(canvases, path.routeId)),
    message: "Every Conditional path must be routed.",
  },
  {
    id: "routes-valid",
    label: "Every route points at a canvas in this workflow",
    serverCodes: [],
    // A route to a deleted canvas would otherwise be dropped silently on save (a Pause would
    // even fall through to the next canvas), and a route back to its own canvas loops forever.
    failsOn: (canvas, canvases) =>
      chosenRoutes(canvas).some(
        (routeId) => routeId === canvas.id || !canvases.some((candidate) => candidate.id === routeId),
      ),
    message: "A route points at a deleted canvas or back at its own canvas.",
  },
  {
    id: "number-comparisons",
    label: "Greater/less-than only compares number fields",
    serverCodes: [],
    failsOn: (canvas, canvases) => {
      if (canvas.type !== "conditional") return false;
      const isNumber = workflowNumberVariableTest(canvases);
      return canvas.paths.some((path) =>
        path.criteria.some(
          (criterion) =>
            WORKFLOW_NUMBER_ONLY_OPERATORS.has(criterion.operator) &&
            Boolean(criterion.variable.trim()) &&
            !isNumber(criterion.variable),
        ),
      );
    },
    message: "Greater/less-than can only compare number fields.",
  },
  {
    id: "pause-unit",
    label: "Every Pause has a wait unit",
    serverCodes: [],
    failsOn: (canvas) => canvas.type === "pause" && !canvas.pause.unit,
    message: "Every Pause needs a unit (hours, days or working days).",
  },
  {
    id: "task-tasks",
    label: "Every Task canvas has ≥1 task",
    serverCodes: ["TASK_EMPTY"],
    failsOn: (canvas) => canvas.type === "task" && canvas.tasks.length === 0,
    message: "Every Task canvas needs at least one task.",
  },
  {
    id: "webservice-url",
    label: "Web-service canvases have a URL",
    serverCodes: ["WEBSERVICE_NO_URL"],
    failsOn: (canvas) => canvas.type === "webservice" && !canvas.webService.url.trim(),
    message: "Web-service canvases need a URL.",
  },
  {
    id: "webservice-body",
    label: "Web-service bodies are valid JSON",
    serverCodes: [],
    failsOn: (canvas) =>
      canvas.type === "webservice" &&
      workflowMethodSendsBody(canvas.webService.method) &&
      !isValidWorkflowBodyTemplate(canvas.webService.body ?? ""),
    message: "A web-service body must be valid JSON, with each {{variable}} inside quotes.",
  },
  {
    id: "file-types",
    label: "No file field allows exe/bat/ps1",
    serverCodes: ["FIELD_SETTINGS_BAD"],
    // One message per offending field (it names the field), shown inline on that field too.
    check: (canvases) =>
      canvases.flatMap(blockedFileFields).map((field) => fileTypeMessage(field.label || "Untitled")),
  },
];

export const runClientValidation = (canvases: WorkflowCanvas[]): WorkflowValidationRow[] =>
  WORKFLOW_VALIDATION_RULES.map((rule) => {
    const messages = rule.failsOn
      ? once(
          canvases.some((canvas, index) => rule.failsOn?.(canvas, canvases, index)),
          rule.message ?? "",
        )
      : rule.check?.(canvases) ?? [];
    return { id: rule.id, label: rule.label, passed: messages.length === 0, messages };
  });

/**
 * Validation issues placed where they belong (Epic 80429: each validation issue is shown inline
 * on the stage or field it belongs to), by canvas id and by form-field id. Workflow-level rules
 * ("must end with a Finalize canvas") have no single home and stay in the checklist only.
 */
export interface WorkflowInlineIssues {
  canvas: Record<string, string[]>;
  field: Record<string, string[]>;
}

const pushIssue = (bucket: Record<string, string[]>, id: string, message: string) => {
  const list = bucket[id] ?? [];
  if (!list.includes(message)) list.push(message);
  bucket[id] = list;
};

export const issuesByCanvas = (canvases: WorkflowCanvas[]): WorkflowInlineIssues => {
  const inline: WorkflowInlineIssues = { canvas: {}, field: {} };
  canvases.forEach((canvas, index) => {
    WORKFLOW_VALIDATION_RULES.forEach((rule) => {
      if (rule.failsOn?.(canvas, canvases, index)) pushIssue(inline.canvas, canvas.id, rule.message ?? "");
    });
    blockedFileFields(canvas).forEach((field) => {
      const message = fileTypeMessage(field.label || "Untitled");
      pushIssue(inline.canvas, canvas.id, message);
      pushIssue(inline.field, field.id, message);
    });
  });
  return inline;
};

/** Adds the server issues: by stageId onto its canvas, and by fieldKey onto that field too. */
export const withServerIssues = (
  inline: WorkflowInlineIssues,
  issues: WorkflowValidationIssueDto[],
  canvases: WorkflowCanvas[],
): WorkflowInlineIssues => {
  const merged: WorkflowInlineIssues = {
    canvas: Object.fromEntries(Object.entries(inline.canvas).map(([id, list]) => [id, [...list]])),
    field: Object.fromEntries(Object.entries(inline.field).map(([id, list]) => [id, [...list]])),
  };
  issues.forEach((issue) => {
    const canvas = canvases.find((candidate) => candidate.id === `wf-stage-${issue.stageId}`);
    if (!canvas) return;
    pushIssue(merged.canvas, canvas.id, issue.message);
    const field = issue.fieldKey
      ? canvas.fields.find((candidate) => candidate.fieldKey === issue.fieldKey)
      : undefined;
    if (field) pushIssue(merged.field, field.id, issue.message);
  });
  return merged;
};

/** Folds the server's issues into the client rows: a matching code fails its row (keeping the
 *  client's exact wording), anything else becomes a row of its own with the server's message. */
export const mergeServerIssues = (
  rows: WorkflowValidationRow[],
  issues: WorkflowValidationIssueDto[],
): WorkflowValidationRow[] => {
  const merged = rows.map((row) => ({ ...row, messages: [...row.messages] }));
  const extra: WorkflowValidationRow[] = [];

  issues.forEach((issue, index) => {
    const rule = WORKFLOW_VALIDATION_RULES.find((candidate) =>
      candidate.serverCodes.includes(issue.code),
    );
    const row = rule && merged.find((candidate) => candidate.id === rule.id);
    if (row) {
      // The client wording wins when the client caught it too; otherwise the server saw something
      // the screen didn't (e.g. a row from another session), and its own message says what.
      if (row.passed) row.messages.push(issue.message);
      row.passed = false;
      return;
    }
    extra.push({ id: `server-${issue.code}-${index}`, label: issue.message, passed: false, messages: [] });
  });

  return [...merged, ...extra];
};

export const allRowsPassed = (rows: WorkflowValidationRow[]) => rows.every((row) => row.passed);
